import { createApi } from '@reduxjs/toolkit/query/react';

import {
  MaterialRequisitionDocumentRequest,
  MaterialRequisitionDocumentResponse,
  MaterialRequisitionFormRequest,
  MaterialRequisitionFormResponse,
  MaterialRequisitionItemFormRequest,
  MaterialRequisitionItemFormResponse,
} from '@Types/api/form';
import {
  ApprovalHistory,
  CopyRequest,
  RequestListSearchRequest,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import { ApiBaseName, ApiReducerPath } from '@Enum/index';
import { PAGE_SIZE, TABLE_PAGE_DEFAULT } from '@Features/constants';
import {
  LedgerResources,
  MaterialResources,
} from '@Types/api/common/dictionary';
import { StoreOverheadResponse } from '@Types/api/form/materialRequisition/MaterialStoreOverheadResponse';
import { customBaseQuery } from '@Utils/api/customBaseQuery';
import { RequisitionCodeDescRequest } from '@Types/api/form/materialRequisition/RequisitionCodeDescItemRequest';
import { RequisitionCodeDescItemResponse } from '@Types/api/form/materialRequisition/RequisitionCodeDescItemResponse';

export const materialRequestApi = createApi({
  tagTypes: ['materialRequest', 'materialRequestItem'],
  reducerPath: ApiReducerPath.API_MATERIAL_REQUEST,
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getMaterialRequests: builder.mutation<
      RequestListSearchResponse<MaterialRequisitionFormResponse>,
      RequestListSearchRequest
    >({
      query: ({
        page = TABLE_PAGE_DEFAULT,
        size = PAGE_SIZE,
        sortBy = 'requisitionId',
        sortOrder = 'desc',
        searchValue,
        filters = [],
      }) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/search`,
        method: 'post',
        body: {
          searchValue: searchValue,
          searchCriteriaList: [
            {
              searchCriterias: filters,
              dataOperator: 'and',
            },
          ],
          dataOperator: 'and',
          requestService: 'MR',
        },
        params: { page, size, 'sort-by': sortBy, 'sort-order': sortOrder },
      }),
    }),
    cancelRequisitionRequest: builder.mutation<null, string>({
      query: (requisitionId) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/cancel`,
        method: 'post',
        params: { requisitionId },
      }),
    }),
    submitRequest: builder.mutation<string, string>({
      query: (requisitionId) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/submit`,
        method: 'post',
        params: { requisitionId },
      }),
    }),
    postRequisitionForm: builder.mutation<
      MaterialRequisitionFormResponse,
      MaterialRequisitionFormRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.REQUISITION}/requisition`,
        method: 'post',
        body,
      }),
    }),
    getRequisition: builder.query<MaterialRequisitionFormResponse, string>({
      query: (id) => ({
        url: `${ApiBaseName.REQUISITION}/requisition`,
        method: 'get',
        params: { requisitionId: id },
      }),
    }),
    getRequisitionItem: builder.query<
      MaterialRequisitionItemFormResponse,
      string
    >({
      query: (id) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/item`,
        method: 'get',
        params: { requisitionItemId: id },
      }),
      keepUnusedDataFor: 0,
    }),
    postRequisitionItemForm: builder.mutation<
      MaterialRequisitionItemFormResponse,
      MaterialRequisitionItemFormRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/item`,
        method: 'post',
        body,
      }),
    }),
    deleteRequisitionItemForm: builder.mutation<null, Array<string | number>>({
      query: (requisitionItemId) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/item`,
        method: 'delete',
        params: { requisitionItemId },
      }),
    }),
    getRequisitionCodeDescItems: builder.mutation<
      RequisitionCodeDescItemResponse[],
      RequisitionCodeDescRequest
    >({
      query: ({ reqId, reqItemId = null }) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/material-budget-plant-data`,
        method: 'post',
        body: {
          reqId,
          reqItemId,
        },
      }),
    }),
    getRequisitionItems: builder.mutation<
      RequestListSearchResponse<MaterialRequisitionItemFormResponse>,
      RequestListSearchRequest & { requisitionId?: number }
    >({
      query: ({
        page = 0,
        size = 20,
        sortBy = 'reqId',
        sortOrder = 'desc',
        searchValue,
        filters = [],
        requisitionId,
      }) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/item/search`,
        method: 'post',
        body: {
          searchValue,
          searchCriteriaList: [
            {
              searchCriterias: [
                {
                  name: 'reqId',
                  value: requisitionId,
                  operator: 'eq',
                },
                ...filters,
              ],
              dataOperator: 'and',
            },
          ],
          dataOperator: 'and',
        },
        params: { page, size, 'sort-by': sortBy, 'sort-order': sortOrder },
      }),
    }),
    getRequisitionDocuments: builder.mutation<
      Array<MaterialRequisitionDocumentResponse>,
      string
    >({
      query: (id) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/${id}/document`,
        method: 'get',
      }),
    }),
    postRequisitionDocument: builder.mutation<
      MaterialRequisitionDocumentResponse,
      MaterialRequisitionDocumentRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/${body.reqId}/document`,
        method: 'post',
        body,
      }),
    }),
    deleteRequisitionDocument: builder.mutation<
      null,
      {
        requisitionId: number;
        body: Array<MaterialRequisitionDocumentResponse>;
      }
    >({
      query: ({ requisitionId, body }) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/${requisitionId}/document`,
        method: 'delete',
        body,
      }),
    }),
    getMaterialResources: builder.mutation<
      RequestListSearchResponse<MaterialResources>,
      {
        requisitionId: number;
        searchValue: string;
      }
    >({
      query: ({ requisitionId, searchValue }) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/material`,
        method: 'get',
        params: {
          requisitionId,
          searchValue,
        },
      }),
    }),
    getMaterialResourceById: builder.mutation<
      MaterialResources,
      {
        materialId: string;
      }
    >({
      query: ({ materialId }) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/material/${materialId}`,
        method: 'get',
      }),
    }),
    getLedgerResources: builder.mutation<
      RequestListSearchResponse<LedgerResources>,
      {
        requisitionId: number;
        searchValue: string;
      }
    >({
      query: ({ requisitionId, searchValue }) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/ledger`,
        method: 'get',
        params: {
          requisitionId,
          searchValue,
        },
      }),
    }),
    getLedgerResourceById: builder.mutation<
      LedgerResources,
      { ledgerId: string }
    >({
      query: ({ ledgerId }) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/ledger/${ledgerId}`,
        method: 'get',
      }),
    }),
    getStoreOverhead: builder.mutation<
      StoreOverheadResponse | null,
      { compId: number; ccId: number; ccTypeId: number }
    >({
      query: ({ compId, ccId, ccTypeId }) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/store-overhead`,
        method: 'get',
        params: { compId, ccid: ccId, ccTypeId },
      }),
      transformResponse(response: StoreOverheadResponse) {
        const filtred = response && response?.storeId ? response : null;
        return filtred;
      },
    }),
    getRequisitionCommodityClass: builder.query<number, number>({
      query: (requisitionId: number) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/commodity-class`,
        method: 'get',
        params: { requisitionId },
      }),
    }),
    getRequisitionUnit: builder.mutation<
      { unitName: string; unitID: number },
      string | number
    >({
      query: (requisitionItemId: string | number) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/unit`,
        method: 'get',
        params: { requisitionItemId },
      }),
    }),
    getApprovalHistory: builder.mutation<ApprovalHistory[], string>({
      query: (id) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/history`,
        method: 'get',
        params: {
          requisitionId: id,
        },
      }),
    }),
    postCopyRequisition: builder.mutation<string, CopyRequest>({
      query: (body) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/copy`,
        method: 'post',
        body,
      }),
    }),
  }),
});

export const {
  useGetRequisitionCommodityClassQuery,
  useGetRequisitionCodeDescItemsMutation,
  usePostCopyRequisitionMutation,
  useGetApprovalHistoryMutation,
  useGetRequisitionUnitMutation,
  useGetStoreOverheadMutation,
  useCancelRequisitionRequestMutation,
  useGetRequisitionQuery,
  useGetMaterialRequestsMutation,
  useSubmitRequestMutation,
  usePostRequisitionFormMutation,
  usePostRequisitionItemFormMutation,
  useDeleteRequisitionItemFormMutation,
  useGetRequisitionItemsMutation,
  useGetRequisitionDocumentsMutation,
  usePostRequisitionDocumentMutation,
  useDeleteRequisitionDocumentMutation,
  useGetLedgerResourceByIdMutation,
  useGetLedgerResourcesMutation,
  useGetMaterialResourceByIdMutation,
  useGetMaterialResourcesMutation,
  useGetRequisitionItemQuery,
} = materialRequestApi;
